import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import { Add as AddIcon, Clear as ClearIcon } from '@mui/icons-material';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';
import { useSnackbar } from 'hooks/use-snackbar';
import { getAreas } from 'api/warehouse/get-areas';
import { GetAreasVm } from 'api/warehouse/get-areas.vm';
import { AreaVm, AreaTypeEnum } from 'api/warehouse/area.vm';
import { createArea } from 'api/warehouse/create-area';
import { useSearchParams } from 'react-router-dom';

const AreasPage = () => {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const [areas, setAreas] = useState<AreaVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentArea, setCurrentArea] = useState<Partial<AreaVm>>({
    name: '',
    type: AreaTypeEnum.RECEPTION,
  });
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const warehouseId = searchParams.get('warehouseId');

  const fetchAreas = async (offset: string | null = null, reset: boolean = false) => {
    if (!warehouseId) return;

    setLoading(true);
    getAreas(warehouseId, offset || '', {
      success: (response: GetAreasVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setAreas(reset ? response.objects : [...areas, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter áreas:', err);
        snackbar.show('Falha ao carregar áreas', null, 'error');
        setLoading(false);
      },
    });
  };

  const fetchWarehouses = () => {
    getWarehouses('', {
      success: (response: GetWarehousesVm) => {
        setWarehouses(response.objects);
        if (response.objects.length) {
          setSearchParams((prev) => {
            prev.set('warehouseId', response.objects[0].id);
            return prev;
          });
        }
      },
      error: (err) => {
        console.error('Falha ao obter armazéns:', err);
        snackbar.show('Falha ao carregar armazéns', null, 'error');
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    if (warehouseId) {
      fetchAreas(null, true);
    } else {
      setAreas([]);
    }
  }, [warehouseId]);

  const handleCreateArea = async () => {
    if (!warehouseId || !currentArea.id || !currentArea.name || !currentArea.type) {
      snackbar.show('Por favor preencha todos os campos obrigatórios', null, 'warning');
      return;
    }

    createArea(
      warehouseId,
      {
        id: currentArea.id,
        name: currentArea.name,
        type: currentArea.type,
      },
      {
        success: () => {
          snackbar.show('Área criada com sucesso', null, 'success');
          fetchAreas(null, true);
          setOpenDialog(false);
          setCurrentArea({
            id: '',
            name: '',
            type: AreaTypeEnum.RECEPTION,
          });
        },
        error: (err) => {
          snackbar.show(err.message || 'Falha ao guardar área', null, 'error');
        },
      }
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Áreas
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setCurrentArea({
              name: '',
              type: AreaTypeEnum.RECEPTION,
            });
            setOpenDialog(true);
          }}
          disabled={!warehouseId}
        >
          Adicionar Área
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={6}>
            <FormControl fullWidth>
              <InputLabel id="warehouse-select-label">Selecionar Armazém</InputLabel>
              <Select
                labelId="warehouse-select-label"
                value={warehouseId || ''}
                label="Selecionar Armazém"
                onChange={(e) => {
                  setSearchParams((prev) => {
                    prev.set('warehouseId', e.target.value);
                    return prev;
                  });
                }}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{warehouse.name}</Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchParams((prev) => {
                  prev.delete('warehouseId');
                  return prev;
                });
              }}
              fullWidth
              startIcon={<ClearIcon />}
              disabled={!warehouseId}
              sx={{ height: '56px' }}
            >
              Limpar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Tipo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {areas.map((area) => (
              <TableRow key={`${area.name}-${area.type}`} hover>
                <TableCell>{area.name}</TableCell>
                <TableCell>{area.type}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={() => fetchAreas(nextOffset)} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : areas.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais áreas para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhuma área encontrada
          </Typography>
        )}
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Nova Área</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="ID/Código de Barras da Área"
              fullWidth
              value={currentArea.id || ''}
              onChange={(e) => setCurrentArea({ ...currentArea, id: e.target.value })}
              required
            />
            <TextField
              label="Nome da Área"
              fullWidth
              value={currentArea.name || ''}
              onChange={(e) => setCurrentArea({ ...currentArea, name: e.target.value })}
              required
            />
            <FormControl fullWidth>
              <InputLabel id="area-type-label">Tipo de Área</InputLabel>
              <Select
                labelId="area-type-label"
                value={currentArea.type || AreaTypeEnum.RECEPTION}
                label="Tipo de Área"
                onChange={(e) => setCurrentArea({ ...currentArea, type: e.target.value as AreaTypeEnum })}
              >
                {Object.values(AreaTypeEnum).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateArea} disabled={!currentArea.name || !currentArea.type}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AreasPage;
